import { useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { getUTM, trackEvent, waLink, trackConversion } from "@/lib/whatsapp";
import { useSettings } from "@/context/SettingsContext";

const SERVICES = [
  "YouTube Video Promotion", "YouTube Ads Campaign Management", "YouTube Channel Promotion",
  "Audience Targeting", "Campaign Strategy", "Creator Growth Consulting",
  "YouTube SEO Consulting", "Analytics & Performance Reporting", "Not sure yet",
];
const BUDGETS = ["Under $250 / month", "$250 – $500 / month", "$500 – $1,000 / month", "$1,000 – $5,000 / month", "$5,000+ / month"];
const GOALS = ["More views & reach", "More subscribers", "Product / brand awareness", "Lead generation", "Launch a new video", "Not sure — need advice"];

export default function LeadForm({ compact = false, defaultService = "", source = "website", onSuccess }) {
  const { settings } = useSettings();
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    full_name: "", whatsapp: "", email: "", youtube_channel_url: "", youtube_video_url: "",
    budget: "", service: defaultService, target_audience: "", country: "", goal: "", message: "",
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.whatsapp || !form.email) {
      toast.error("Please fill your name, WhatsApp number and email.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", { ...form, ...getUTM(), source });
      trackEvent("lead_submit", { service: form.service, source });
      const ads = settings?.analytics || {};
      if (ads.google_ads_id && ads.google_ads_conversion_label) {
        trackConversion(`${ads.google_ads_id}/${ads.google_ads_conversion_label}`);
      }
      setDone(true);
      toast.success("Request received! We'll contact you shortly.");
      onSuccess?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    const waConfirm = waLink("918382890707", "Hi NewStarAds, I submitted a YouTube promotion inquiry and would like to discuss my campaign.");
    return (
      <div className="text-center py-10 px-6" data-testid="lead-success">
        <div className="mx-auto w-14 h-14 grid place-items-center rounded-full bg-green-100 text-green-600 mb-4">
          <ShieldCheck className="w-7 h-7" />
        </div>
        <h3 className="font-display text-2xl font-bold">Thank you — request received!</h3>
        <p className="mt-2 text-muted-foreground max-w-sm mx-auto">
          Our team will review your details and reach out on WhatsApp/email shortly. No spam, no bots — just a real conversation about your goals.
        </p>
        <a href={waConfirm} target="_blank" rel="noreferrer" onClick={() => trackEvent("whatsapp_click", { location: "lead_confirmation" })}>
          <Button className="mt-6 rounded-full bg-green-600 hover:bg-green-700" data-testid="lead-success-whatsapp">
            <MessageCircle className="w-4 h-4 mr-1" /> Continue on WhatsApp
          </Button>
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="lead-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Full Name *"><Input data-testid="lead-name" value={form.full_name} onChange={set("full_name")} placeholder="Your name" required /></Field>
        <Field label="WhatsApp Number *"><Input data-testid="lead-whatsapp" value={form.whatsapp} onChange={set("whatsapp")} placeholder="+91 ..." required /></Field>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Email *"><Input data-testid="lead-email" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" required /></Field>
        <Field label="Country"><Input data-testid="lead-country" value={form.country} onChange={set("country")} placeholder="India" /></Field>
      </div>
      {!compact && (
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="YouTube Channel URL"><Input data-testid="lead-channel" value={form.youtube_channel_url} onChange={set("youtube_channel_url")} placeholder="https://youtube.com/@..." /></Field>
          <Field label="YouTube Video URL"><Input data-testid="lead-video" value={form.youtube_video_url} onChange={set("youtube_video_url")} placeholder="https://youtu.be/..." /></Field>
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Service Required">
          <Select value={form.service} onValueChange={set("service")}>
            <SelectTrigger data-testid="lead-service"><SelectValue placeholder="Select a service" /></SelectTrigger>
            <SelectContent>{SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Monthly Marketing Budget">
          <Select value={form.budget} onValueChange={set("budget")}>
            <SelectTrigger data-testid="lead-budget"><SelectValue placeholder="Select budget" /></SelectTrigger>
            <SelectContent>{BUDGETS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </div>
      {!compact && (
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Campaign Goal">
            <Select value={form.goal} onValueChange={set("goal")}>
              <SelectTrigger data-testid="lead-goal"><SelectValue placeholder="Select goal" /></SelectTrigger>
              <SelectContent>{GOALS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Target Audience"><Input data-testid="lead-audience" value={form.target_audience} onChange={set("target_audience")} placeholder="e.g. gamers 18-24 in India" /></Field>
        </div>
      )}
      <Field label="Message"><Textarea data-testid="lead-message" value={form.message} onChange={set("message")} placeholder="Tell us about your channel and goals..." rows={compact ? 2 : 3} /></Field>
      <Button type="submit" disabled={loading} className="w-full h-12 rounded-full text-base" data-testid="lead-submit">
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Request Free Consultation"}
      </Button>
      <p className="text-xs text-muted-foreground text-center">
        By submitting you agree to our Privacy Policy. We never share your data or send spam.
      </p>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-widest font-semibold text-slate-500">{label}</Label>
      {children}
    </div>
  );
}
